import { fmtDate, VbNewsArticle } from '@viewbid/ui';
import { NewsArticlePublicResponse } from '@viewbid/ts-angular-client';

/** Plain-text preview length for the index rows (CSS clamps to 2 lines). */
const PREVIEW_MAX_LENGTH: number = 240;

function displayDate(article: NewsArticlePublicResponse): string | undefined {
    return fmtDate(article.publishedAt) || undefined;
}

/** Flattens editor HTML to plain text and truncates it for the preview cell. */
function toPreview(html: string): string {
    const tmp: HTMLElement = document.createElement('DIV');
    tmp.innerHTML = html;
    const text: string = (tmp.textContent || tmp.innerText || '').trim();
    return text.length > PREVIEW_MAX_LENGTH
        ? text.substring(0, PREVIEW_MAX_LENGTH) + '...'
        : text;
}

/** Rich-text bodies open their links in a new tab. */
function addTargetBlankToLinks(html: string | undefined): string {
    if (!html) return '';
    const tmp: HTMLElement = document.createElement('DIV');
    tmp.innerHTML = html;
    const links: HTMLCollectionOf<HTMLAnchorElement> =
        tmp.getElementsByTagName('a');
    for (let i: number = 0; i < links.length; i++) {
        links[i].setAttribute('target', '_blank');
    }
    return tmp.innerHTML;
}

/**
 * Maps a published news article to the `vb-news-list` row shape: title,
 * plain-text preview, and display date. The API has no lot code / category /
 * read time, so the row kicker and READ metadata degrade gracefully.
 */
export function mapNewsListItem(
    article: NewsArticlePublicResponse,
): VbNewsArticle {
    return {
        id: article.id,
        title: article.title,
        preview: toPreview(article.body),
        date: displayDate(article),
    };
}

/**
 * Maps a published news article to the `vb-news-article` reading shape. The
 * body renders through `bodyHtml` (editor markup, links retargeted to open in
 * a new tab). No `preview` on purpose — the lede would just duplicate the
 * body's opening text.
 */
export function mapNewsArticleDetail(
    article: NewsArticlePublicResponse,
): VbNewsArticle {
    return {
        id: article.id,
        title: article.title,
        date: displayDate(article),
        author: article.authorName ?? article.publisherName ?? undefined,
        bodyHtml: addTargetBlankToLinks(article.body),
    };
}
